import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai/utils';
import {
  SearchBar,
  ListingCard,
  ListingRow,
  Input,
  PageIntro,
  PageTitle,
  Pager,
  Button,
  Checkbox,
  BatchActions,
  LayoutViewSwitcher,
  LayoutView,
  AddAssetsToCollections,
  AddAssetsToShare,
  BulkCutoutModal,
  CampaignLinksModal,
  Select,
  Dropdown,
  InfoSection,
  ActiveFilters,
  FiltersDrawerHeader,
} from 'src/components';
import { useSearchQuery } from 'src/api/queries/assetsQueries';
import { getThumbnailUrl, thumbnailVersion } from 'src/utils/previews';
import { CaretRight, CaretDown, FolderPlus, Stack, Trash, ShareNetwork, AssetGetLink, Crop, ArrowsClockwise } from 'src/common/icons';
import { ReprocessModal, MAX_REPROCESS_ASSETS } from 'src/components/ReprocessFlow';
import styles from './styles.module.scss';
import messages from './messages';
import useFileSpinPicker from 'src/hooks/useFileSpinPicker';
import useFiltersDrawer from 'src/hooks/useFiltersDrawer';
import { lockScroll, unlockScroll } from 'src/utils/bodyScroll';
import { useCollectionMutation } from 'src/api/mutations/collectionMutations';
import { queryNames } from 'src/api/utils/queryNames';
import { CollectionResponse } from 'src/types/TCollection';
import { bulkDeleteAssets } from 'src/api/functions/assetsApi';
import CreateCollectionModal from 'src/components/CreateCollectionModal';
import useComponentHooks from './hooks';
import {
  ITEMS_PER_PAGE_LOCAL_KEY,
  MAX_BULK_CUTOUT_ASSETS,
  PAGE_ACTIONS,
  PENDING_UPLOAD_POLL_INTERVAL_MS,
  PENDING_UPLOAD_TIMEOUT_MS,
} from './constants';
import { statusOptions } from 'src/api/queries/assetsQueries';
import { getSchemas } from 'src/api/functions/schemaApi';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useWritesPaused } from 'src/hooks/useWritesPaused';
import { useDeliveryInsightsEnabled } from 'src/hooks/useDeliveryInsightsEnabled';
import { useRemoveBackgroundAvailable } from 'src/hooks/useRemoveBackgroundAvailable';
import { ISubmitEvent } from '@rjsf/core';
import SearchForm from 'src/components/SearchForm';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import {
  pendingUploadsAtom,
  PendingUpload,
  lastUploadAtom,
  LAST_UPLOAD_TTL_MS,
} from 'src/shared/pendingUploads';
import { AgentNudgeRow } from 'src/chat/nudges';
import { pickerModeAtom } from 'src/shared/pickerMode';
import BoardingTour from 'src/components/BoardingTour';
import GettingStartedChecklist from 'src/components/GettingStartedChecklist';
import { useOnboardingProgressQuery } from 'src/api/queries/onboardingQueries';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { useRememberContentTypes } from 'src/chat/useRememberContentTypes';

/**
 *  @name Dashboard
 *  @module pages/Dashboard
 *
 *  @route GET /
 *  @route GET /assets
 *
 *  @description The Dashboard page.
 *
 *  @summary
 *  Filter state lives in the canonical `dashboardFiltersAtom` (see
 *  `src/shared/dashboardFilters.ts`). This component owns only the
 *  draft-input pieces (RJSF schema-form working state, dropdown selections
 *  before submit) and pulls everything else from the atom. Every active
 *  filter is rendered as a pill by `<ActiveFilters />`, which walks the
 *  descriptor table — adding a new filter requires adding a descriptor
 *  entry and nothing else.
 */

// Coerce AI-provided field values so they satisfy the JSON schema shape the
// RJSF form expects. The AI proxy often sends a scalar for array-typed fields
// (e.g. { tags: "test" } instead of { tags: ["test"] }), which RJSF silently
// drops. Without this the schema form renders empty even though the server
// search still returned matching assets.
function coerceFieldsToSchema(
  fields: Record<string, any>,
  schemaDef: any,
): Record<string, any> {
  const properties = schemaDef?.properties;
  if (!properties || !fields || typeof fields !== 'object') return fields;
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(fields)) {
    const propType = properties[key]?.type;
    if (propType === 'array' && !Array.isArray(value)) {
      if (value == null || value === '') {
        out[key] = [];
      } else {
        out[key] = [value];
      }
    } else if (propType === 'number' || propType === 'integer') {
      const num = typeof value === 'number' ? value : Number(value);
      out[key] = Number.isFinite(num) ? num : value;
    } else if (propType === 'boolean' && typeof value !== 'boolean') {
      out[key] = value === 'true' || value === true;
    } else {
      out[key] = value;
    }
  }
  return out;
}

export const Dashboard = (): JSX.Element => {
  const hooks = useComponentHooks();
  const accessToken = useAccessToken();
  // A paused account (trial at a cap, spend limit, trial ended) refuses
  // uploads at nginx's auth check with a bare 403, so don't offer one.
  const { paused: writesPaused } = useWritesPaused();
  const apply = hooks.apply;
  const filterState = hooks.filterState;
  // "Find duplicates" mode lists exact copies of one asset in place of a
  // search. It shows as a filter pill with the asset's thumbnail; any new
  // search or filter leaves the mode.
  const duplicatesOf = filterState.duplicatesOf;
  const [settingsData, setSettingData] = useAtom(addonsSettingsAtom);
  const deliveryInsightsEnabled = useDeliveryInsightsEnabled();
  const removeBackgroundAvailable = useRemoveBackgroundAvailable();
  const [pendingUploads, setPendingUploads] = useAtom(pendingUploadsAtom);
  const [lastUpload, setLastUpload] = useAtom(lastUploadAtom);
  const pickerMode = useAtomValue(pickerModeAtom);

  const {
    data: fetchedData,
    isLoading,
    refetch,
    isRefetching,
  } = useSearchQuery(
    hooks.serverPayload,
    {
      onSuccess: (results) => {
        hooks.state.totalPages !== results.total_pages && hooks.state.setTotalPages(results.total_pages);
        hooks.refs.searchHash.current = results.search_result_id;
      },
    },
    {
      // The backend indexes uploads asynchronously; while placeholders are
      // pending, poll until they show up in the search results (the expiry
      // effect below clears stragglers and stops the polling).
      refetchInterval: pendingUploads.length > 0 ? PENDING_UPLOAD_POLL_INTERVAL_MS : false,
    },
  );

  const { formatMessage } = useIntl();

  // ----- Schema-search "draft" state ---------------------------------------
  // These mirror the user's current dropdown selection and form working
  // values before they submit. The committed selection lives on the atom
  // (`filterState.schema`); a useEffect below syncs from atom -> draft so
  // AI intents and pill removals propagate cleanly.

  const [schemaSearchMode, setSchemaSearchMode] = useState(false);
  const [schemaFaceSearchMode, setFaceSchemaSearchMode] = useState(false);
  const [schema, setSchema] = useState<any>();
  const [faceSchema, setFaceSchema] = useState<any>();
  const [selectedSchemaId, setSelectedSchemaId] = useState<any>('-1');
  const [selectedSchemaIdFace, setSelectedSchemaIdFace] = useState<any>('-1');
  const [selectSchemaOptions, setSelectSchemaOptions] = useState<any>([]);
  const [selectSchemaOptionsFace, setSelectSchemaOptionsFace] = useState<any>([]);
  const [schemaIdDataMapping, setSchemaIdDataMapping] = useState<any>({});
  const [schemaIdDataMappingFace, setSchemaIdDataMappingFace] = useState<any>({});
  const [schemaKeyValueMapping, setSchemaKeyValueMapping] = useState<any>({});
  const [schemaKeyValueMappingFace, setSchemaKeyValueMappingFace] = useState<any>({});
  const [metadata, setMetaData] = useState<any>({});
  const [searchMetaData, setSearchMetaData] = useState<any>({});

  const sortByKeyAscending = (options: Array<{ key: string | number; value: string }>) => {
    return options.sort((a, b) => Number(a.key) - Number(b.key));
  };

  const fetchSchemas = async () => {
    const result = await getSchemas(accessToken);
    if (!result.data) return;
    const keyValueMapping: any = {};
    const idSchemaMapping: any = {};
    let options = [
      { value: 'None Selected', key: '-1' },
      ...result.data.map((schema: any) => {
        keyValueMapping[schema.id.toString()] = schema.name.en;
        idSchemaMapping[schema.id] = schema;
        return {
          value: `${schema.name.en} (ID: ${schema.id.toString()})`,
          key: schema.id.toString(),
        };
      }),
    ];

    options = sortByKeyAscending(options);

    setSchemaKeyValueMapping(keyValueMapping);
    setSchemaIdDataMapping(idSchemaMapping);
    setSelectSchemaOptions(options);
    setSchemaKeyValueMappingFace(keyValueMapping);
    setSchemaIdDataMappingFace(idSchemaMapping);
    setSelectSchemaOptionsFace(options);
  };

  useEffect(() => {
    fetchSchemas();
  }, []);

  // Sync atom -> schema-draft state. Fires after chat-driven intents, pill
  // clears, Clear-all, and on initial schema load. Also upgrades any
  // placeholder schema name (e.g. `Schema 7` written by the chat-proxy
  // before this dashboard had loaded the schema registry) to the friendly
  // human-readable name once it becomes known.
  useEffect(() => {
    const sel = filterState.schema;
    if (sel != null && schemaIdDataMapping[sel.id] != null) {
      const schemaDef = schemaIdDataMapping[sel.id]?.schema;
      setSelectedSchemaId(String(sel.id));
      setSchema(schemaDef);
      setSchemaSearchMode(true);
      const coerced = coerceFieldsToSchema(sel.fields as Record<string, any>, schemaDef);
      setMetaData(coerced);
      setSearchMetaData(coerced);

      const realName = schemaKeyValueMapping[String(sel.id)];
      if (realName && sel.name !== realName) {
        apply({ schema: { ...sel, name: realName } });
      }
    } else if (sel == null) {
      setSelectedSchemaId('-1');
      setSchema(undefined);
      setSchemaSearchMode(false);
      setMetaData({});
      setSearchMetaData({});
    }
  }, [filterState.schema, schemaIdDataMapping, schemaKeyValueMapping, apply]);

  // Hydrate the `addons_criteria` UI from the addons settings only — no
  // change to behaviour from the previous version.
  useEffect(() => {
    if (hooks.queries.collections.status === 'success' && hooks.queries.collections.data?.status === 'OK') {
      localStorage.setItem('assetCollection', JSON.stringify(hooks.queries.collections.data));
    }
  }, [hooks.queryClient, hooks.queries]);

  const basketMutation = useCollectionMutation(Number(hooks.queries.collections.data?.basketData?.data?.id), {
    onError: (error, newData, context: any) => {
      hooks.queryClient.setQueryData([queryNames.collection, null], context.previousData);

      hooks.alerts.showAlert(hooks.errorMessages.getErrorMessage(error.message));
    },
    onMutate: (data) => {
      const previousData = hooks.queryClient.getQueryData<CollectionResponse>([queryNames.collection, null]);
      hooks.queryClient.setQueryData<CollectionResponse | undefined>(
        [queryNames.collection, null],
        (old: CollectionResponse | undefined) => {
          if (typeof old !== 'undefined') {
            old.data.assets = (old?.data?.assets ?? [])
              .filter((id) => !(data?.deletions ?? []).includes(id))
              .concat(data?.additions ?? []);
          }
          return old;
        },
      );
      return { previousData };
    },
    onSuccess: () => {
      hooks.alerts.showSuccess('Request succeeded. Refreshing data...');
    },
  });

  const { data: onboardingProgress } = useOnboardingProgressQuery();
  const onboardingProgressMutation = useUpdateOnboardingProgressMutation();

  // Track successful uploads from the picker `complete` payload so they show
  // up immediately as "Processing" placeholders while the search index
  // catches up. Replace-mode uploads target an existing asset, so no
  // placeholder is needed for them.
  const addPendingUploads = (d: any) => {
    if (pickerMode === 'replace') return;
    const files: any[] = d?.payload?.files ?? [];
    const now = Date.now();
    const uploaded: PendingUpload[] = files
      .filter((f) => f?.success && f?.id)
      .map((f) => ({
        id: String(f.id),
        name: f.name ?? '',
        contentType: f.content_type ?? '',
        size: f.size,
        addedAt: now,
      }));
    if (uploaded.length === 0) return;

    // Drives the post-upload agent nudge. Recorded before the de-dupe below
    // because an asset the index already knows about still deserves a nudge.
    setLastUpload({ ids: uploaded.map((u) => u.id), at: now });

    const alreadyListed = new Set<string>(fetchedData?.result ?? []);
    setPendingUploads((prev) => {
      const known = new Set(prev.map((p) => p.id));
      return [...prev, ...uploaded.filter((u) => !known.has(u.id) && !alreadyListed.has(u.id))];
    });
  };

  const { uploadContent } = useFileSpinPicker({
    events: {
      complete: (d) => {
        refetch();
        addPendingUploads(d);
        if (!onboardingProgress?.checklist?.first_asset_uploaded) {
          onboardingProgressMutation.mutate({ checklist: { first_asset_uploaded: true } });
        }
      },
      close: () => unlockScroll(),
      open: () => lockScroll(),
    },
  });

  useEffect(() => {
    hooks.state.updateDeletedAssets([]);
    return () => {
      setPendingUploads([]);
      setLastUpload(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop placeholders as soon as the search index returns the real asset.
  useEffect(() => {
    if (!fetchedData?.result || pendingUploads.length === 0) return;
    const indexed = new Set<string>(fetchedData.result);
    if (pendingUploads.some((p) => indexed.has(p.id))) {
      setPendingUploads((prev) => prev.filter((p) => !indexed.has(p.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedData]);

  // Expire placeholders that never appeared (e.g. active filters exclude
  // them, or indexing is unusually slow) so polling stops and the user
  // learns why nothing showed up.
  useEffect(() => {
    if (pendingUploads.length === 0) return;
    const expireAt = Math.min(...pendingUploads.map((p) => p.addedAt)) + PENDING_UPLOAD_TIMEOUT_MS;
    const timer = setTimeout(() => {
      const cutoff = Date.now() - PENDING_UPLOAD_TIMEOUT_MS;
      const remaining = pendingUploads.filter((p) => p.addedAt > cutoff);
      if (remaining.length < pendingUploads.length) {
        hooks.alerts.showInfo(formatMessage(messages.uploadsStillProcessing));
      }
      setPendingUploads(remaining);
    }, Math.max(0, expireAt - Date.now()));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUploads]);

  const data = useMemo(() => {
    const result = fetchedData?.result.filter((asset: string) => !hooks.state.deletedAssets.includes(asset));
    // An upload still in flight isn't a known copy, so duplicates mode skips the placeholders.
    if (pendingUploads.length === 0 || duplicatesOf) return { ...fetchedData, result };

    // Prepend pending uploads as NOT_READY placeholders. ListingCard /
    // ListingRow already render a "Processing" badge and icon thumbnail for
    // that status, so no component changes are needed.
    const visible = new Set<string>(result ?? []);
    const placeholders = pendingUploads.filter((p) => !visible.has(p.id));
    const placeholderExtended = placeholders.reduce<Record<string, any>>((acc, p) => {
      acc[p.id] = {
        name: p.name,
        size: p.size,
        content_type: p.contentType,
        status: 'NOT_READY',
        upload_time: new Date(p.addedAt).toISOString(),
        trashed: false,
      };
      return acc;
    }, {});

    return {
      ...fetchedData,
      result: [...placeholders.map((p) => p.id), ...(result ?? [])],
      extended_result: { ...placeholderExtended, ...(fetchedData?.extended_result ?? {}) },
    };
  }, [fetchedData, hooks.state.deletedAssets, pendingUploads, duplicatesOf]);

  // Content types for the agent nudges' image/video gates. Read from the
  // search payload the grid already renders from — the ['asset', id] query
  // cache is only populated once a detail modal has been opened, so it would
  // report 'unknown' for most of a selection.
  useRememberContentTypes(data?.extended_result);
  const contentTypesFor = useCallback(
    (ids: string[]) => ids.map((id) => data?.extended_result?.[id]?.content_type ?? ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const lastUploadFresh =
    !!lastUpload && Date.now() - lastUpload.at < LAST_UPLOAD_TTL_MS;


  const handleAddAsset = () => {
    uploadContent();
  };

  const handleAddSelectedAssetsToBasket = () => {
    if (basketMutation.isLoading) return;
    basketMutation.mutateAsync({ additions: hooks.state.selectedAssets });
    hooks.state.updateSelectedAssets([]);
  };

  // Persist the user's items-per-page preference.
  useEffect(() => {
    localStorage.setItem(ITEMS_PER_PAGE_LOCAL_KEY, String(filterState.limitPerPage));
  }, [filterState.limitPerPage]);

  // The chat-side writers (`useSSEStream`, `ActionCard`) call
  // `applyAiSearchIntentAtom` directly, so the dashboard no longer needs a
  // bridge-consumer effect for `aiSearchIntentAtom`.

  useEffect(() => {
    if (isRefetching) return;
    refetch();
  }, [hooks.state.refreshValue]);

  useEffect(() => {
    hooks.state.setIsRefetching(isRefetching);
  }, [isRefetching]);

  const handleOnAssetSelectChange = (assetId: string) => {
    const newSelectedAssets = hooks.state.selectedAssets.includes(assetId)
      ? hooks.state.selectedAssets.filter((id) => id !== assetId)
      : [...hooks.state.selectedAssets, assetId];
    hooks.state.updateSelectedAssets(newSelectedAssets);
  };

  const handleSelectAllAssets = (selected: boolean) => {
    const newSelectedAssets = selected ? data.result : [];
    hooks.state.updateSelectedAssets(newSelectedAssets);
  };

  const handleMoveToTrash = async () => {
    if (hooks.state.isDeletingAssets) return;

    hooks.state.setIsDeletingAssets(true);

    try {
      const response: any = await bulkDeleteAssets(hooks.state.selectedAssets, hooks.accessToken);

      if (typeof response.status === 'number' && ![200, 202].includes(response.status)) {
        throw response;
      }

      const newDeletedAssets = [...hooks.state.deletedAssets, ...hooks.state.selectedAssets];

      hooks.state.updateDeletedAssets(newDeletedAssets);
      hooks.alerts.showSuccess(`Successfully deleted selected assets`);
    } catch (error: any) {
      const errorMessage = hooks.errorMessages.getErrorMessage(error.message);
      hooks.alerts.showAlert(errorMessage);
    } finally {
      hooks.state.setIsDeletingAssets(false);
      hooks.state.updateSelectedAssets([]);
    }
  };

  useEffect(() => {
    return () => {
      hooks.state.updateSelectedAssets([]);
    };
  }, []);

  // Entering or leaving "Find duplicates" mode (chip X, pill X, a new search)
  // swaps the whole result set, so a selection made in one must not carry
  // into the other.
  const duplicatesOfId = duplicatesOf?.id ?? null;
  const previousDuplicatesOfId = useRef(duplicatesOfId);
  useEffect(() => {
    if (previousDuplicatesOfId.current !== duplicatesOfId) {
      previousDuplicatesOfId.current = duplicatesOfId;
      hooks.state.updateSelectedAssets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicatesOfId]);

  const canReprocess = hooks.roleManager.isAdmin || hooks.roleManager.isManager;

  const handleOnClickAction = (option: string) => {
    switch (option) {
      case PAGE_ACTIONS.ADD_TO_COLLECTIONS:
        const addAssetsToCollectionsElement = <AddAssetsToCollections />;
        return hooks.modal.showModal(addAssetsToCollectionsElement);
      case PAGE_ACTIONS.ADD_TO_BASKET:
        return handleAddSelectedAssetsToBasket();
      case PAGE_ACTIONS.MOVE_TO_TRASH:
        return handleMoveToTrash();
      case PAGE_ACTIONS.CREATE_COLLECTION:
        const routeToCollections = () => hooks.history.push('/collections');
        const element = <CreateCollectionModal onCreateCollection={routeToCollections} />;
        return hooks.modal.showModal(element);
      case PAGE_ACTIONS.ADD_TO_SHARE:
        const addAssetsToShareElement = <AddAssetsToShare assetIds={hooks.state.selectedAssets} />;
        return hooks.modal.showModal(addAssetsToShareElement);
      case PAGE_ACTIONS.GET_CAMPAIGN_LINKS:
        const campaignLinksElement = (
          <CampaignLinksModal mode="owner" assetIds={hooks.state.selectedAssets} />
        );
        return hooks.modal.showModal(campaignLinksElement);
      case PAGE_ACTIONS.CUTOUT_BACKGROUND:
        const bulkCutoutElement = <BulkCutoutModal assetIds={hooks.state.selectedAssets} />;
        return hooks.modal.showModal(bulkCutoutElement);
      case PAGE_ACTIONS.REPROCESS:
        return hooks.modal.showModal(
          <ReprocessModal
            assetIds={hooks.state.selectedAssets}
            contentTypes={contentTypesFor(hooks.state.selectedAssets)}
          />,
        );
    }
  };

  const pageActions = useMemo(() => {
    const actions = [
      {
        key: PAGE_ACTIONS.ADD_TO_COLLECTIONS,
        value: 'Add to Collections',
        noBorderBottom: false,
        icon: <FolderPlus />,
        optionDisabled: hooks.state.selectedAssets.length === 0,
      },
      {
        key: PAGE_ACTIONS.CREATE_COLLECTION,
        value: 'Create new Collection',
        noBorderBottom: false,
        icon: <FolderPlus />,
        optionDisabled: hooks.state.selectedAssets.length === 0,
      },
      ...(!hooks.roleManager.isUser ? [{
        key: PAGE_ACTIONS.ADD_TO_SHARE,
        value: 'Add to Share',
        noBorderBottom: false,
        icon: <ShareNetwork />,
        optionDisabled: hooks.state.selectedAssets.length === 0,
      }] : []),
      ...(!hooks.roleManager.isUser ? [{
        key: PAGE_ACTIONS.GET_CAMPAIGN_LINKS,
        value: 'Get campaign links',
        noBorderBottom: false,
        icon: <AssetGetLink />,
        optionDisabled: hooks.state.selectedAssets.length === 0 || !deliveryInsightsEnabled,
      }] : []),
      // Matte outcomes only, so it is gated on the background-removal addon.
      // The generative outcomes stay per-asset (they read each photo and take
      // their own instructions), which is why this is not "Cut out / change
      // background" like the asset-view menu item.
      ...(!hooks.roleManager.isUser ? [{
        key: PAGE_ACTIONS.CUTOUT_BACKGROUND,
        value: 'Remove background',
        noBorderBottom: false,
        icon: <Crop />,
        optionDisabled:
          hooks.state.selectedAssets.length === 0 ||
          hooks.state.selectedAssets.length > MAX_BULK_CUTOUT_ASSETS ||
          !removeBackgroundAvailable,
      }] : []),
      // Admin + Manager, as the backend allows (saas#1262).
      ...(canReprocess ? [{
        key: PAGE_ACTIONS.REPROCESS,
        value: 'Reprocess…',
        noBorderBottom: false,
        icon: <ArrowsClockwise />,
        optionDisabled:
          writesPaused ||
          hooks.state.selectedAssets.length === 0 ||
          hooks.state.selectedAssets.length > MAX_REPROCESS_ASSETS,
      }] : []),
      {
        key: PAGE_ACTIONS.ADD_TO_BASKET,
        value: 'Add to Basket',
        noBorderBottom: false,
        icon: <Stack />,
        optionDisabled: hooks.state.selectedAssets.length === 0,
      },
    ];

    if (!hooks.roleManager.isUser) {
      actions.push({
        key: PAGE_ACTIONS.MOVE_TO_TRASH,
        value: 'Move to Trash',
        noBorderBottom: false,
        icon: <Trash />,
        optionDisabled: hooks.state.selectedAssets.length === 0,
      });
    }

    return actions;
  }, [hooks.roleManager.isUser, hooks.state.selectedAssets, deliveryInsightsEnabled, removeBackgroundAvailable, canReprocess, writesPaused]);

  const handleFilenameChange = (value: string) => hooks.state.setFileName(value);
  const handleContentTypeInputChange = (value: string) => hooks.state.setCustomContentInput(value);

  const [showFreeFormData, setShowFreeFormData] = useState<boolean>(false);
  const [showFreeFormFields, setShowFreeFormFields] = useState<boolean>(false);

  type RenderSearchFormProps = {
    schema?: any;
    metadata: any;
    onSubmit: ((e: ISubmitEvent<any>, nativeEvent: React.FormEvent<HTMLFormElement>) => any) | undefined;
    onCancel: () => void;
  };

  const handleSubmit = () => {
    document.getElementById('hiddenSubmitButton')?.click();
  };

  const RenderSearchForm: React.FC<RenderSearchFormProps> = ({ schema, metadata, onSubmit, onCancel }) => {
    return (
      <>
        {schemaSearchMode && (
          <div ref={formRef} className={cn(styles.formContainer, { [styles.expanded]: schemaSearchMode })}>
            <SearchForm schema={schema} metadata={metadata} onSubmit={onSubmit} onCancel={onCancel} />
          </div>
        )}
        <div className={styles.formButtonsContainer}>
          <Button size="small" mode="tertiary" type="button" onClick={handleCancel} disabled={!schemaSearchMode}>
            Cancel
          </Button>
          <Button size="small" mode="primary" type="button" onClick={handleSubmit} disabled={!schemaSearchMode}>
            Submit
          </Button>
        </div>
      </>
    );
  };

  const RenderFaceSearchForm: React.FC<RenderSearchFormProps> = ({ schema, metadata, onSubmit, onCancel }) => {
    return (
      <>
        {!showFreeFormData && schemaFaceSearchMode && (
          <>
            <div ref={formRef} className={cn(styles.formFaceContainer, { [styles.expanded]: schemaFaceSearchMode })}>
              <SearchForm schema={schema} metadata={metadata} onSubmit={onSubmit} onCancel={onCancel} />
            </div>
            <div className={styles.formFaceButtonsContainer}>
              <Button
                size="small"
                mode="tertiary"
                type="button"
                onClick={handleCancel}
                disabled={!schemaFaceSearchMode}
              >
                Cancel
              </Button>
              <Button size="small" mode="primary" type="button" onClick={handleSubmit} disabled={!schemaFaceSearchMode}>
                Submit
              </Button>
            </div>
          </>
        )}
      </>
    );
  };

  const renderDropdownValue = (value: string) => {
    if (!value || value.length <= 28) {
      return value;
    }
    return `${value.slice(0, 25)}...`;
  };

  const processOptions = (data: any) => {
    const updatedData: any = {};

    for (const key in data) {
      const value = data[key];
      if (
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && value !== null && Object.keys(value).length === 0) ||
        value === false
      ) {
        continue;
      }

      updatedData[key] = typeof value === 'object' ? JSON.stringify(value) : value;
    }

    return updatedData;
  };

  const submitSearchData = async (schemaObject: any) => {
    const options = processOptions(schemaObject?.formData);
    setMetaData(schemaObject?.formData);
    setSearchMetaData(schemaObject?.formData);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (filterState.face) {
      apply({
        freeFormFilters: options,
        page: 1,
        hash: '',
      });
    } else {
      const id = Number(selectedSchemaId);
      const name = schemaKeyValueMapping[selectedSchemaId] ?? `Schema ${id}`;
      apply({
        schema: { id, name, fields: options },
        page: 1,
        hash: '',
      });
    }
  };

  const formRef = useRef<any>(null);

  useEffect(() => {
    if (schemaSearchMode && formRef.current) {
      const height = formRef.current.scrollHeight > 400 ? 400 : formRef.current.scrollHeight;
      formRef.current.style.maxHeight = `${height}px`;
    }
  }, [schemaSearchMode]);

  const handleCancel = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    hooks.clearSchemaAndFreeForm();
    setSchemaSearchMode(false);
    setSelectedSchemaId('-1');
    setSelectedSchemaIdFace('-1');
    setMetaData({});
    setSearchMetaData({});
    setShowFreeFormFields(false);
  };

  const onFaceSearchClose = () => {
    hooks.state.setFace(null);
    hooks.state.setSearch('');
    hooks.enhancedFaceRemoval();
    setSchemaSearchMode(false);
    setSelectedSchemaId('-1');
    setSelectedSchemaIdFace('-1');
    setMetaData({});
    setSearchMetaData({});
  };

  const isFreeFormData = useMemo(() => selectedSchemaIdFace === '-1', [selectedSchemaIdFace]);

  useEffect(() => {
    setShowFreeFormData(isFreeFormData);
  }, [isFreeFormData]);

  const handleFreeFormSubmit = (fieldsData: Record<string, string>) => {
    const newoptions = processOptions(fieldsData);
    apply({
      freeFormFilters: newoptions,
      page: 1,
      hash: '',
    });
  };

  const handleCancelFreeFormData = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    hooks.clearSchemaAndFreeForm();
    setSchemaSearchMode(false);
    setSelectedSchemaId('-1');
    setSelectedSchemaIdFace('-1');
    setMetaData({});
    setSearchMetaData({});
    setShowFreeFormFields(false);
  };

  const foldableFilterComponent = (
    <div style={{ minHeight: '400px', minWidth: '400px' }}>
      {filterState.face ? (
        <>
          <FoldableFilter title={'Filters'}>
            <div>
              <Dropdown
                className={styles.dropD}
                isMultiple={false}
                options={selectSchemaOptionsFace}
                onClick={(key) => {
                  if (key === '-1') {
                    handleCancel();
                  } else {
                    setSelectedSchemaIdFace(key);
                    setFaceSchema(schemaIdDataMappingFace[Number(key)]?.schema);
                    setFaceSchemaSearchMode(true);
                    setSearchMetaData({});
                  }
                }}
                reduceOptionSize={true}
              >
                {selectSchemaOptionsFace?.length > 0 ? (
                  selectedSchemaIdFace === '-1' ? (
                    'None Selected'
                  ) : (
                    `${renderDropdownValue(
                      schemaKeyValueMappingFace[selectedSchemaIdFace],
                    )} (ID: ${selectedSchemaIdFace})`
                  )
                ) : (
                  <br />
                )}
              </Dropdown>
            </div>
            {schemaFaceSearchMode && !showFreeFormData && (
              <div className={cn(styles.formFaceNewContainer, { [styles.expanded]: schemaFaceSearchMode })}>
                <RenderFaceSearchForm
                  schema={faceSchema}
                  metadata={searchMetaData}
                  onSubmit={submitSearchData}
                  onCancel={handleCancel}
                />
              </div>
            )}
          </FoldableFilter>
        </>
      ) : (
        <div className={styles.foldableContainer}>
          <h4>
            {<CaretRight />} {'Filters'}
          </h4>
          <div style={{ maxWidth: '330px' }}>
            <InfoSection text={formatMessage(messages.searchInfo)} />
          </div>
        </div>
      )}
    </div>
  );

  const [isFaceSearchEnabled, setIsFaceSearchEnabled] = useState<boolean>(false);

  // On mobile the filter rail behaves as a slide-in drawer with a scrim
  // backdrop. Desktop layout is unchanged.
  const filtersDrawer = useFiltersDrawer({
    visible: hooks.state.filtersVisible,
    setVisible: hooks.state.setFiltersVisible,
  });

  return (
    <>
      <BoardingTour
        title="Assets"
        description="This page as all the assets have uploaded by the user"
        path="/assets"
        uniqueId="ASSETS"
      />
      <GettingStartedChecklist totalAssets={duplicatesOf ? undefined : data?.total_files} />
      <div className={styles.pageWrapper} data-test="page-content">
        <div>
          <PageTitle
            title={'Assets'}
            className={styles.pageTitle}
            counter={data?.total_files}
            rightContainer={
              hooks.roleManager.isUser ? null : (
                // A disabled button fires no mouse events, so the reason sits on
                // a wrapper as a native tooltip.
                <span title={writesPaused ? formatMessage(messages.addPaused) : undefined}>
                  <Button
                    size="small"
                    onClick={handleAddAsset}
                    disabled={writesPaused}
                    data-test="add-assets-btn"
                  >
                    <FormattedMessage {...messages.add} />
                  </Button>
                </span>
              )
            }
          />
          <PageIntro inset overview="Your images, videos, documents and their metadata." />
          <SearchBar
            search={hooks.state.search}
            sortKeys={[
              { key: 'upload_time_range', value: 'Upload Date' },
              { key: 'file_name', value: 'File Name' },
              { key: 'content_type', value: 'Content Type' },
              { key: 'file_size_range', value: 'File Size' },
            ]}
            onSelectSort={(sortKey) => hooks.state.setSorting(sortKey)}
            onSearch={(searchTerm) => hooks.state.setSearch(searchTerm)}
            onSearchFace={(img) => hooks.enhancedFaceSearch(img)}
            onSearchFaceCancel={onFaceSearchClose}
            onClickFiltersButton={() => hooks.state.setFiltersVisible(!hooks.state.filtersVisible)}
            searchFace={settingsData?.FACE_RECOGNITION?.added && settingsData?.FACE_RECOGNITION?.enabled}
            foldableFaceFilter={foldableFilterComponent}
            faceSearchEnabled={isFaceSearchEnabled}
            setFaceSearchEnabled={setIsFaceSearchEnabled}
          />
          <section className={styles.pageContainer}>
            {filtersDrawer.drawerOpen && !isFaceSearchEnabled && (
              <div
                className={styles.mobileScrim}
                onClick={filtersDrawer.closeIfMobile}
                aria-hidden
              />
            )}
            {hooks.state.filtersVisible && !isFaceSearchEnabled && (
              <div className={cn([styles.filtersContent])}>
                <FiltersDrawerHeader onClose={filtersDrawer.closeIfMobile} />
                <FoldableFilter title={'File Name'}>
                  <Input value={hooks.state.fileName} onChange={handleFilenameChange} />
                </FoldableFilter>
                <FoldableFilter title={'Content Type'} dataTest="filter-click">
                  <Checkbox
                    label="All"
                    checked={filterState.contentType === ''}
                    onChange={() => {
                      apply({ contentType: '', customContentInput: '', page: 1, hash: '' });
                      hooks.state.setCustomContentInput('');
                    }}
                    radio={true}
                  />
                  <Checkbox
                    label="Image"
                    checked={filterState.contentType === 'image'}
                    onChange={() => {
                      const isSelected = filterState.contentType === 'image';
                      apply({
                        contentType: isSelected ? '' : 'image',
                        customContentInput: '',
                        page: 1,
                        hash: '',
                      });
                      hooks.state.setCustomContentInput('');
                    }}
                    radio={true}
                    datatest="image-click"
                  />
                  <Checkbox
                    label="Video"
                    checked={filterState.contentType === 'video'}
                    onChange={() => {
                      const isSelected = filterState.contentType === 'video';
                      apply({
                        contentType: isSelected ? '' : 'video',
                        customContentInput: '',
                        page: 1,
                        hash: '',
                      });
                      hooks.state.setCustomContentInput('');
                    }}
                    radio={true}
                  />
                  <Checkbox
                    label="Audio"
                    checked={filterState.contentType === 'audio'}
                    onChange={() => {
                      const isSelected = filterState.contentType === 'audio';
                      apply({
                        contentType: isSelected ? '' : 'audio',
                        customContentInput: '',
                        page: 1,
                        hash: '',
                      });
                      hooks.state.setCustomContentInput('');
                    }}
                    radio={true}
                  />
                  <div>
                    <Checkbox
                      label="Custom"
                      checked={
                        filterState.contentType !== '' &&
                        !['image', 'video', 'audio'].includes(filterState.contentType)
                      }
                      onChange={() => {
                        const isSelected =
                          filterState.contentType !== '' &&
                          !['image', 'video', 'audio'].includes(filterState.contentType);
                        apply({
                          contentType: isSelected ? '' : '*',
                          customContentInput: '',
                          page: 1,
                          hash: '',
                        });
                        hooks.state.setCustomContentInput('');
                      }}
                      radio={true}
                    />

                    <div style={{ marginTop: '1rem', maxWidth: '290px', marginLeft: '2.3rem' }}>
                      <Input
                        placeholder="Eg: application/pdf"
                        value={hooks.state.customContentInput}
                        onChange={handleContentTypeInputChange}
                        disabled={
                          filterState.contentType === '' ||
                          filterState.contentType === 'image' ||
                          filterState.contentType === 'video' ||
                          filterState.contentType === 'audio'
                        }
                      />
                    </div>
                  </div>
                </FoldableFilter>
                <FoldableFilter title={'Upload Date'}>
                  <div className={styles.rangeValues}>
                    <Input
                      type="datetime-local"
                      label="Start Date :"
                      step={1}
                      value={hooks.startDateInputValue}
                      onChange={hooks.handleStartDateChange}
                    />
                    <Input
                      type="datetime-local"
                      label="End Date :"
                      step={1}
                      value={hooks.endDateInputValue}
                      onChange={hooks.handleEndDateChange}
                    />
                  </div>
                </FoldableFilter>
                <FoldableFilter title={'Asset Status'}>
                  <Select
                    className={styles.Select}
                    options={statusOptions}
                    value={filterState.status}
                    onChange={(v) => apply({ status: v, page: 1, hash: '' })}
                  />
                </FoldableFilter>
                <FoldableFilter title={'Addons Processed'}>
                  <Checkbox
                    label="On-demand Image (ODI)"
                    checked={filterState.addonsCriteria.includes('ON_DEMAND_IMAGE')}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const newAddons = checked
                        ? [...filterState.addonsCriteria, 'ON_DEMAND_IMAGE']
                        : filterState.addonsCriteria.filter((addon) => addon !== 'ON_DEMAND_IMAGE');
                      apply({ addonsCriteria: newAddons, page: 1, hash: '' });
                    }}
                  />
                  <Checkbox
                    label="Video Storyboard"
                    checked={filterState.addonsCriteria.includes('STORYBOARD')}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const newAddons = checked
                        ? [...filterState.addonsCriteria, 'STORYBOARD']
                        : filterState.addonsCriteria.filter((addon) => addon !== 'STORYBOARD');
                      apply({ addonsCriteria: newAddons, page: 1, hash: '' });
                    }}
                  />
                  <Checkbox
                    label="Video Transcode"
                    checked={filterState.addonsCriteria.includes('VIDEO_TRANSCODE')}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const newAddons = checked
                        ? [...filterState.addonsCriteria, 'VIDEO_TRANSCODE']
                        : filterState.addonsCriteria.filter((addon) => addon !== 'VIDEO_TRANSCODE');
                      apply({ addonsCriteria: newAddons, page: 1, hash: '' });
                    }}
                  />
                  {settingsData?.FACE_RECOGNITION?.added && (
                    <Checkbox
                      label="Face Recognition"
                      checked={filterState.addonsCriteria.includes('FACE_RECOGNITION')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const newAddons = checked
                          ? [...filterState.addonsCriteria, 'FACE_RECOGNITION']
                          : filterState.addonsCriteria.filter((addon) => addon !== 'FACE_RECOGNITION');
                        apply({ addonsCriteria: newAddons, page: 1, hash: '' });
                      }}
                    />
                  )}
                  {settingsData?.IMAGE_ANALYSIS?.added && (
                    <Checkbox
                      label="Image Auto-tags"
                      checked={filterState.addonsCriteria.includes('IMAGE_ANALYSIS')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const newAddons = checked
                          ? [...filterState.addonsCriteria, 'IMAGE_ANALYSIS']
                          : filterState.addonsCriteria.filter((addon) => addon !== 'IMAGE_ANALYSIS');
                        apply({ addonsCriteria: newAddons, page: 1, hash: '' });
                      }}
                    />
                  )}
                </FoldableFilter>
                <FoldableFilter title={'Generated Assets'}>
                  <Checkbox
                    label="Show only generated assets"
                    checked={filterState.derivedOnly}
                    onChange={(e) =>
                      apply({ derivedOnly: e.target.checked, page: 1, hash: '' })
                    }
                  />
                </FoldableFilter>
                <FoldableFilter title={'Asset Versions'}>
                  <Checkbox
                    label="Awaiting release"
                    checked={filterState.pendingReleaseOnly}
                    onChange={(e) =>
                      apply({ pendingReleaseOnly: e.target.checked, page: 1, hash: '' })
                    }
                  />
                </FoldableFilter>
                <FoldableFilter title={'Asset Schema'}>
                  <div>
                    <Dropdown
                      className={styles.dropD}
                      isMultiple={false}
                      options={selectSchemaOptions}
                      onClick={(key) => {
                        if (key === '-1') {
                          handleCancel();
                        } else {
                          setSelectedSchemaId(key);
                          setSchema(schemaIdDataMapping[Number(key)]?.schema);
                          setSchemaSearchMode(true);
                          setMetaData({});
                        }
                      }}
                    >
                      {selectSchemaOptions?.length > 0 ? (
                        selectedSchemaId === '-1' ? (
                          'None Selected'
                        ) : (
                          `${renderDropdownValue(schemaKeyValueMapping[selectedSchemaId])} (ID: ${selectedSchemaId})`
                        )
                      ) : (
                        <br />
                      )}
                    </Dropdown>
                    <div className={cn(styles.formNewContainer, { [styles.expanded]: schemaSearchMode })}>
                      <RenderSearchForm
                        schema={schema}
                        metadata={metadata}
                        onSubmit={submitSearchData}
                        onCancel={handleCancel}
                      />
                    </div>
                  </div>
                </FoldableFilter>
              </div>
            )}
            <div className={cn([styles.pageContent])}>
              <div className={styles.pageActions}>
                <BatchActions
                  showSelectAll={data?.result?.length > 0}
                  onSelectAll={handleSelectAllAssets}
                  pageActions={{
                    options: pageActions,
                    onClick: handleOnClickAction,
                  }}
                />
                <LayoutViewSwitcher />
              </div>

              <AgentNudgeRow
                surface="selection"
                assetIds={hooks.state.selectedAssets}
                contentTypes={contentTypesFor(hooks.state.selectedAssets)}
              />

              {hooks.state.selectedAssets.length === 0 && lastUploadFresh && lastUpload && (
                <AgentNudgeRow
                  surface="post-upload"
                  claimSelection
                  assetIds={lastUpload.ids}
                  contentTypes={contentTypesFor(lastUpload.ids)}
                  onDismiss={() => setLastUpload(null)}
                />
              )}

              <div className={styles.filtersWrapper}>
                <ActiveFilters />
              </div>

              <LayoutView
                data={data?.result}
                emptyDataMessage={duplicatesOf ? formatMessage(messages.noDuplicates) : 'No assets found'}
                // While polling for pending uploads, keep the current list
                // (with its placeholders) on screen instead of flashing the
                // shimmer state on every background refetch.
                isLoading={isLoading || (isRefetching && pendingUploads.length === 0)}
                classNames={{
                  cardView: 'listingGridWrapper',
                }}
                cardView={(assetId: string) => (
                  <ListingCard
                    key={assetId}
                    id={assetId}
                    siblings={data?.result}
                    imageUrl={getThumbnailUrl({
                      width: 300,
                      height: 300,
                      id: assetId,
                      jwt: hooks.accessToken,
                      version: thumbnailVersion(data?.extended_result[assetId]),
                    })}
                    name={data?.extended_result[assetId]?.name}
                    size={data?.extended_result[assetId]?.size}
                    contentType={data?.extended_result[assetId]?.content_type}
                    trashed={data?.extended_result[assetId]?.trashed}
                    status={data?.extended_result[assetId]?.status}
                    onSelectChange={() => handleOnAssetSelectChange(assetId)}
                    isSelected={hooks.state.selectedAssets.includes(assetId)}
                    isGenerated={!!data?.extended_result[assetId]?.data?._filespin_original_asset_id}
                    originalAssetId={data?.extended_result[assetId]?.data?._filespin_original_asset_id}
                    versions={data?.extended_result[assetId]?.versions}
                    error={data?.extended_result[assetId]?.errors}
                  />
                )}
                listView={(assetId: string) => (
                  <ListingRow
                    key={assetId}
                    id={assetId}
                    siblings={data?.result}
                    imageUrl={getThumbnailUrl({
                      width: 100,
                      height: 100,
                      id: assetId,
                      jwt: hooks.accessToken,
                      version: thumbnailVersion(data?.extended_result[assetId]),
                    })}
                    name={data?.extended_result[assetId]?.name}
                    size={data?.extended_result[assetId]?.size}
                    uploadTime={data?.extended_result[assetId]?.upload_time}
                    contentType={data?.extended_result[assetId]?.content_type}
                    trashed={data?.extended_result[assetId]?.trashed}
                    status={data?.extended_result[assetId]?.status}
                    onSelectChange={() => handleOnAssetSelectChange(assetId)}
                    isSelected={hooks.state.selectedAssets.includes(assetId)}
                    isGenerated={!!data?.extended_result[assetId]?.data?._filespin_original_asset_id}
                    originalAssetId={data?.extended_result[assetId]?.data?._filespin_original_asset_id}
                    versions={data?.extended_result[assetId]?.versions}
                    error={data?.extended_result[assetId]?.errors}
                  />
                )}
              />

              {hooks.state.totalPages > 1 ? (
                <div className={styles.pagerWrapper}>
                  <Pager
                    currentPage={filterState.page}
                    totalPages={hooks.state.totalPages}
                    onChange={(page) =>
                      apply({ page, hash: hooks.refs.searchHash.current })
                    }
                    itemsPerPage={filterState.limitPerPage}
                    // duplicates come 30 to a page, whatever the preference
                    showItemsPerPage={!duplicatesOf}
                    onItemsPerPageChange={(newLimit) =>
                      apply({ page: 1, hash: '', limitPerPage: newLimit })
                    }
                    datatest="pager-button-click"
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '20px' }}></div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

const FoldableFilter: React.FC<{ title: string; dataTest?: string }> = ({ title, children, dataTest }) => {
  const [isRevealed, setIsRevealed] = useState(() => {
    return title === 'File Name' || title === 'Filters';
  });

  return (
    <div className={styles.foldableContainer} data-test={dataTest}>
      <h4 onClick={() => setIsRevealed(!isRevealed)}>
        {isRevealed ? <CaretDown /> : <CaretRight />} {title}
      </h4>
      {isRevealed && <div className={styles.filterChildren}>{children}</div>}
    </div>
  );
};
